import React, { useId, useMemo, useState } from 'react'
import Badge from '@mui/material/Badge'
import Box from '@mui/material/Box'
import ButtonBase from '@mui/material/ButtonBase'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import SwipeableDrawer from '@mui/material/SwipeableDrawer'
import MoreHoriz from '@mui/icons-material/MoreHoriz'
import {
  BOTTOM_NAV_HEIGHT,
  BRAND_NAVY,
  BRAND_RED,
  CREAM,
  LIGHT_CHROME,
  SAFE_AREA_BOTTOM,
  TOUCH_MIN,
} from 'core/theme.tokens'
import type { PageTabOption } from 'contexts/PageTabsContext'
import { isOverflowValue, splitPageTabs } from 'components/splitPageTabs'

export { INLINE_TAB_LIMIT } from 'components/splitPageTabs'

export const tabLabel = (tab: PageTabOption) => {
  if (!tab.badge) {
    return tab.label
  }
  return (
    <Box component='span' sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
      <Badge
        badgeContent={tab.badge}
        color='error'
        sx={{
          '& .MuiBadge-badge': {
            position: 'relative',
            transform: 'none',
            minWidth: 18,
            height: 18,
            fontSize: '0.7rem',
          },
        }}
      >
        <span>{tab.label}</span>
      </Badge>
    </Box>
  )
}

interface MobilePageTabBarProps {
  tabs: PageTabOption[]
  value: string | number
  onChange: (value: string | number) => void
  ariaLabel?: string
  label?: string
}

const itemSx = (selected: boolean, showDivider: boolean) =>
  ({
    flex: 1,
    minWidth: 0,
    minHeight: `calc(${BOTTOM_NAV_HEIGHT}px + ${SAFE_AREA_BOTTOM})`,
    px: 0.75,
    pt: 0,
    pb: SAFE_AREA_BOTTOM,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    color: selected ? CREAM : 'rgba(244, 241, 235, 0.7)',
    fontWeight: selected ? 700 : 500,
    fontSize: 12,
    lineHeight: 1.2,
    textAlign: 'center',
    borderRadius: 0,
    boxSizing: 'border-box',
    bgcolor: selected ? LIGHT_CHROME : BRAND_NAVY,
    borderRight: showDivider ? '1px solid' : 0,
    borderRightColor: 'rgba(244, 241, 235, 0.18)',
  }) as const

const labelSlotSx = (selected: boolean) =>
  ({
    height: BOTTOM_NAV_HEIGHT,
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 0.25,
    boxSizing: 'border-box',
    borderTop: '3px solid',
    borderTopColor: selected ? BRAND_RED : 'transparent',
  }) as const

/**
 * Phone page-tab bar: the first main tabs stay pinned in a stable order.
 * Extra tabs open from More. Selecting a More tab does not reshuffle the row.
 */
export const MobilePageTabBar: React.FC<MobilePageTabBarProps> = ({
  tabs,
  value,
  onChange,
  ariaLabel = 'Page sections',
  label = 'Section',
}) => {
  const reactId = useId()
  const [moreOpen, setMoreOpen] = useState(false)
  const { main, more } = useMemo(() => splitPageTabs(tabs), [tabs])
  const overflowSelected = isOverflowValue(more, value)

  return (
    <Box
      sx={{
        width: '100%',
        minWidth: 0,
        minHeight: `calc(${BOTTOM_NAV_HEIGHT}px + ${SAFE_AREA_BOTTOM})`,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        role='tablist'
        aria-label={ariaLabel}
        sx={{
          display: 'flex',
          alignItems: 'stretch',
          width: '100%',
          flex: 1,
          minHeight: `calc(${BOTTOM_NAV_HEIGHT}px + ${SAFE_AREA_BOTTOM})`,
        }}
      >
        {main.map((tab, index) => {
          const selected = tab.value === value
          const showDivider = index < main.length - 1 || more.length > 0
          return (
            <ButtonBase
              key={String(tab.value)}
              role='tab'
              aria-selected={selected}
              disabled={tab.disabled}
              onClick={() => onChange(tab.value)}
              sx={itemSx(selected, showDivider)}
            >
              <Box sx={labelSlotSx(selected)}>
                <Box
                  component='span'
                  sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    width: '100%',
                  }}
                >
                  {tabLabel(tab)}
                </Box>
              </Box>
            </ButtonBase>
          )
        })}
        {more.length > 0 ? (
          <ButtonBase
            role='tab'
            aria-selected={overflowSelected}
            aria-expanded={moreOpen}
            aria-controls={`${reactId}-more-sheet`}
            aria-label={`More ${label.toLowerCase()}s`}
            onClick={() => setMoreOpen(true)}
            sx={itemSx(overflowSelected, false)}
          >
            <Box sx={labelSlotSx(overflowSelected)}>
              <MoreHoriz sx={{ fontSize: 22 }} />
              <Box component='span' sx={{ fontSize: 11, lineHeight: 1.2 }}>
                More
              </Box>
            </Box>
          </ButtonBase>
        ) : null}
      </Box>
      <SwipeableDrawer
        id={`${reactId}-more-sheet`}
        anchor='bottom'
        open={moreOpen}
        onClose={() => setMoreOpen(false)}
        onOpen={() => setMoreOpen(true)}
        disableSwipeToOpen
        PaperProps={{
          sx: {
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
            pb: SAFE_AREA_BOTTOM,
          },
        }}
      >
        <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
          <Box
            sx={{
              width: 40,
              height: 4,
              bgcolor: 'divider',
              borderRadius: 2,
              mx: 'auto',
              mb: 1,
            }}
          />
          <List aria-label={`More ${label.toLowerCase()}s`}>
            {more.map(tab => (
              <ListItemButton
                key={String(tab.value)}
                selected={tab.value === value}
                disabled={tab.disabled}
                onClick={() => {
                  onChange(tab.value)
                  setMoreOpen(false)
                }}
                sx={{ minHeight: TOUCH_MIN, borderRadius: 1 }}
              >
                <ListItemText primary={tabLabel(tab)} />
              </ListItemButton>
            ))}
          </List>
        </Box>
      </SwipeableDrawer>
    </Box>
  )
}
